import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { destroySession } from "../../helpers/Session";
import toast from "../../components/sweetalert/Sweetalert";
import DataTable from "react-data-table-component";
import UserLeaveLoader from "../../components/loader/UserLeaveLoader";
import Swal from "sweetalert2";
import { useSidebarData } from "../../hooks/sidebarData";

const MyLeaves = () => {
    const STATUS_ACTIVE = 1;

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setSidebarData } = useSidebarData();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken') || '';
    const aimtoken = sessionStorage.getItem('aimtoken') || '';
    const uname = sessionStorage.getItem('uname') || '';
    const name = sessionStorage.getItem('name') || '';
    const email = sessionStorage.getItem('email') || '';
    const level = sessionStorage.getItem('level') || '';
    const department = sessionStorage.getItem('department') || '';
    const departmentslug = sessionStorage.getItem('departmentslug') || '';
    const outlet = sessionStorage.getItem('outletname') || '';
    const outletcode = sessionStorage.getItem('outletcode') || '';

    const [currentPage, setCurrentPage] = useState(1);
    const [rowPerPage, setRowPerPage] = useState(10);

    const [currentDate, setCurrentDate] = useState(null);
    const [noticePeriod, setNoticePeriod] = useState(null);
    const [formSubmit, setFormSubmit] = useState(false);
    const [formData, setFormData] = useState({
        id: 0,
        leaveid: '',
        leave: '',
        leavetype: '',
        startdate: '',
        starttime: '',
        enddate: '',
        endtime: '',
        reason: '',
        attachment: ''
    });

    const userLeaveDataKey = ['userleaves', { uname, currentPage, rowPerPage }];

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'attachment[]') {
            setFormData({ ...formData, attachment: files }); 
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const resetForm = () => {
        setFormData({
            id: 0,
            leaveid: '',
            leave: '',
            leavetype: '',
            startdate: '',
            starttime: '',
            enddate: '',
            endtime: '',
            reason: '',
            attachment: ''
        });
        document.querySelector("input[name='leavetype']").checked = '';
        document.querySelector("input[name='attachment[]']").value = '';
    };

    const cancelLeaveRequestMutation = useMutation({
        mutationFn: async (leaveData) => {
            const res = await axios.put(`leave/${uname}`, leaveData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data?.response;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(userLeaveDataKey);
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = error.response?.data?.response || 'Something went wrong while cancelling leave request!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();               
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleCancelLeaveRequest = (leaveid, date) => {
        Swal.fire({
            text: `Are you sure you want to cancel your leave request for ${date}?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: function(result) {
                return new Promise(function(resolve) {
                    if (result) {
                        cancelLeaveRequestMutation.mutate({
                            leaverequestid: leaveid
                        });
                        resolve();
                    }  
                });
            }
        });
    }

    const handleViewLeaveRequest = (leave, leaveid) => {
        setSidebarData('state', true);
        setSidebarData('key', 'leave-request');
        setSidebarData('label', `${leave} Request Details`);
        setSidebarData('data', {
            uname,
            leaverequestid: leaveid
        });
    }

    const handleViewLeaveRequestStatus = (leave, leaveid, sdate, edate) => {
        setSidebarData('state', true);
        setSidebarData('key', 'leave-request-status');
        setSidebarData('label', `${leave} Request Status`);
        setSidebarData('data', {
            uname,
            leaverequestid: leaveid,
            sdate,
            edate
        });
    }

    const tableColumns = [
        { name: 'Leave', selector: row => row.leave },
        { name: 'Leave Type', selector: row => row.leavetype },
        { name: 'Start Date', selector: row => row.startdate },
        { name: 'End Date', selector: row => row.enddate },
        { name: 'Status', selector: row => row.status },
        { name: 'Action', selector: row => row.action }
    ];

    const { data: userleaves, isLoading: isLoadingUserLeaves, error: errorUserLeaves } = useQuery({
        queryKey: userLeaveDataKey,
        queryFn: async () => {
            const res = await axios.get(`leave/${uname}?page=${currentPage}&rows_per_page=${rowPerPage}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            const data = res.data.response?.userleaves?.map(item => ({
                leave: item.leave,
                leavetype: item.leavetype === 1 ? 'With Pay' : 'Without Pay',
                startdate: item.start_date,
                enddate: item.end_date,
                status: (
                        item.status === 1 
                            ? <span className="label label-primary" onClick={() => handleViewLeaveRequestStatus(item.leave, item.id, item.start_date, item.end_date)}>Pending</span> 
                            : (item.status === 2 
                                ? <span className="label label-success" onClick={() => handleViewLeaveRequestStatus(item.leave, item.id, item.start_date, item.end_date)}>Approved</span> 
                                : (item.status === 3 
                                    ? <span className="label label-danger" onClick={() => handleViewLeaveRequestStatus(item.leave, item.id, item.start_date, item.end_date)}>Disapproved</span> 
                                    : (item.status === 4 
                                        ? <span className="label label-warning" onClick={() => handleViewLeaveRequestStatus(item.leave, item.id, item.start_date, item.end_date)}>Cancelled</span> 
                                        : <span className="label label-primary">Pending</span>)))
                ),
                action: (
                    <div className="buttons">
                        <button type="button" className="btn-view" title="VIEW" onClick={() => handleViewLeaveRequest(item.leave, item.id)}>
                            <i className="fa fa-eye"></i>
                        </button>
                        { ((item.status === 1) ||  (item.status === 2 && item.date_diff < 4))  && (
                            <button type="button" className="btn-cancel" title="CANCEL" onClick={() => handleCancelLeaveRequest(item.id, item.start_date)}>
                                <i className="fa fa-times"></i>
                            </button>
                        )}
                    </div>
                )
            }));

            return { data, totalRows: res.data.response?.totalRows }
        },
        enabled: !!uname && !!currentPage && !!rowPerPage,
        retry: 1
    });
    
    useEffect(() => {
        if (errorUserLeaves) {
            if (errorUserLeaves.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error') }, 1000);
            }
        }
    }, [errorUserLeaves]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleRowsPerPageChange = (newPerPage) => {
        setRowPerPage(newPerPage);
        setCurrentPage(1);
    };

    useEffect(() => {
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];
        setCurrentDate(formattedDate);
        setNoticePeriod(formattedDate);
    }, []);

    const handleSetNoticePeriod = (e) => {
        const selectedOption = e.target.selectedOptions[0];
        const selectedLeaveLabel = selectedOption.value;

        // ISB-1514: Sick Leave and Emergency Leave can be filed for previous dates (no notice period restriction)
        // Vacation Leave requires 2 weeks (14 days) advance notice
        const isSickLeave = selectedLeaveLabel.toLowerCase().includes('sick');
        const isEmergencyLeave = selectedLeaveLabel.toLowerCase().includes('emergency');
        const isVacationLeave = selectedLeaveLabel.toLowerCase().includes('vacation');

        if (isSickLeave || isEmergencyLeave) {
            setNoticePeriod(null); // No minimum date restriction
        } else if (isVacationLeave) {
            // Vacation Leave: 14 days from today
            const minDate = new Date();
            minDate.setDate(minDate.getDate() + 14);
            setNoticePeriod(minDate.toISOString().split('T')[0]);
        } else {
            setNoticePeriod(selectedOption.getAttribute('data-noticeperiod'));
        }

        setFormData({
            ...formData,
            leaveid: selectedOption.getAttribute('data-leaveid'),
            leave: selectedOption.value
        });
    };

    const { data: leaves, error: errorLeaves } = useQuery({
        queryKey: ['leaveselection'],
        queryFn: async () => {
            const res = await axios.get(`leaves?status=${STATUS_ACTIVE}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response?.leaves;
        },
        enabled: !!accesstoken,
        retry: 1
    });

    useEffect(() => {
        if (errorLeaves) {
            if (errorLeaves.response?.data?.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    }, [errorLeaves]);

    // Compact validation function
    const validateForm = () => {
        const requiredFields = ['leave', 'leavetype', 'startdate', 'enddate', 'reason'];
        for (let field of requiredFields) {
            if (!formData[field]) {
                toast.fire({ icon: 'error', title: `${field.charAt(0).toUpperCase() + field.slice(1)} is required!` });
                return false;
            }
        }

        // ISB-1514: Skip notice period validation for Sick Leave and Emergency Leave
        // Vacation Leave: enforce 14-day advance notice
        const isSickLeave = formData.leave.toLowerCase().includes('sick');
        const isEmergencyLeave = formData.leave.toLowerCase().includes('emergency');
        const isVacationLeave = formData.leave.toLowerCase().includes('vacation');

        // Date validation (skip notice period check for Sick Leave and Emergency Leave)
        if (!isSickLeave && !isEmergencyLeave && noticePeriod) {
            const errorMsg = isVacationLeave
                ? 'Vacation Leave must be filed at least 2 weeks in advance!'
                : 'Start and End date must not be earlier than the notice period!';

            if (formData.startdate < noticePeriod || formData.enddate < noticePeriod) {
                toast.fire({ icon: 'error', title: errorMsg });
                return false;
            }
        }

        if (formData.enddate < formData.startdate) {
            toast.fire({ icon: 'error', title: 'End date cannot be earlier than Start date!' });
            return false;
        }

        // Time validation
        if (formData.starttime && formData.endtime) {
            const startDateTime = new Date(`${formData.startdate}T${formData.starttime}`);
            const endDateTime = new Date(`${formData.enddate}T${formData.endtime}`);

            if (startDateTime >= endDateTime) {
                toast.fire({ icon: 'error', title: 'End time must be later than Start time!' });
                return false;
            }
        }

        return true;
    };

    const userLeaveRequestMutation = useMutation({
        mutationFn: async (leaveRequestData) => {
            const res = await axios.post(`leave/${uname}`, leaveRequestData, {
                headers: { 
                    'Authorization': `Bearer ${accesstoken}`,
                    'X-Aim-Token': aimtoken,
                    'Content-Type': 'multipart/form-data' 
                }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            resetForm();
            setFormSubmit(false);
            queryClient.invalidateQueries(userLeaveDataKey);
            toast.fire({ icon: 'success', title: data });
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = error.response?.data?.response || 'Something went wrong while sending leave request!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();               
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleSubmitLeaveRequest = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const formDataToSubmit = new FormData(e.target);
        
        // Add additional form data if necessary
        formDataToSubmit.append('name', name);
        formDataToSubmit.append('email', email);
        formDataToSubmit.append('level', level);
        formDataToSubmit.append('department', department);
        formDataToSubmit.append('departmentcode', departmentslug);
        formDataToSubmit.append('outlet', outlet);
        formDataToSubmit.append('outletcode', outletcode);
        formDataToSubmit.append('leaveid', formData.leaveid);

        Swal.fire({
            text: `Are you sure you want to send this leave request?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: function(result) {
                return new Promise(function(resolve) {
                    if (result) {
                        setFormSubmit(true);
                        userLeaveRequestMutation.mutate(formDataToSubmit); // Submit the leave request
                        resolve();
                    }  
                });
            }
        });
    };
    

    return (
        <div className="my-leave">
            <div className="page-header">
                <div className='title'>
                    <h1>My Leave</h1>
                    <ul className='breadcrumbs'>
                        <li><NavLink to='/leaves/my-leave'>Leave</NavLink></li>
                        <li><span>My Leave</span></li>
                    </ul>
                </div>
            </div>

            <div className="panels">
                <div className="left-panel">
                    <div className="requests">
                        <DataTable 
                            columns={tableColumns} 
                            data={userleaves?.data || []}
                            progressPending={isLoadingUserLeaves} 
                            progressComponent={<UserLeaveLoader />} 
                            pagination
                            paginationServer
                            paginationTotalRows={userleaves?.totalRows || 0}
                            onChangeRowsPerPage={handleRowsPerPageChange}
                            onChangePage={handlePageChange}
                            dense
                            highlightOnHover
                            responsive
                        />
                    </div>
                </div>

                <div className="right-panel">
                    <form onSubmit={(e) => { handleSubmitLeaveRequest(e)}} encType="multipart/form-data">
                        <div className='form-group'>
                            <label>Leave</label>
                            <select name='leave' value={formData.leave} onChange={handleSetNoticePeriod} disabled={formSubmit}>
                                <option value='' data-leaveid="0" data-noticeperiod={currentDate}></option>
                                {
                                    leaves?.map(item => (
                                        <option key={item.id} value={item.leave} data-leaveid={item.id} data-noticeperiod={item.noticeperiod}>{item.leave}</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className='form-group'>
                            <label>Leave Type</label>
                            <div className="radio-group">
                                <label>
                                    <input type='radio' name='leavetype' value="1" onChange={handleInputChange} disabled={formSubmit} />
                                    <span>With Pay</span>
                                </label>
                                <label>
                                    <input type='radio' name='leavetype' value="2" onChange={handleInputChange} disabled={formSubmit} />
                                    <span>Without Pay</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Start</label>
                            <div className="date-group">
                                <div className="input-group">
                                    <div className='form-group'>
                                        <label>Date</label>
                                        <input type='date' name='startdate' value={formData.startdate} min={noticePeriod || undefined} onChange={handleInputChange} disabled={formSubmit} />
                                    </div>
                                    <div className='form-group'>
                                        <label>Time</label>
                                        <input type='time' name='starttime' value={formData.starttime} onChange={handleInputChange} disabled={formSubmit} />
                                        <p className="note">leave empty if full day</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>End</label>
                            <div className="date-group">
                                <div className="input-group">
                                    <div className='form-group'>
                                        <label>Date</label>
                                        <input type='date' name='enddate' value={formData.enddate} min={formData.startdate || noticePeriod || undefined} onChange={handleInputChange} disabled={formSubmit} />
                                    </div>
                                    <div className='form-group'>
                                        <label>Time</label>
                                        <input type='time' name='endtime' value={formData.endtime} onChange={handleInputChange} disabled={formSubmit} />
                                        <p className="note">leave empty if full day</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='form-group'>
                            <label>Reason</label>
                            <textarea rows="3" cols="5" name='reason' value={formData.reason} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Attachment</label>
                            <input type='file' name='attachment[]' accept=".jpeg, .jpg, .png, .doc, .docx, .pdf" multiple onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='button-group'>
                            <button type='submit' disabled={formSubmit}>
                                {formSubmit ? <span>SAVING <i className="fa fa-spinner fa-spin"></i></span> : 'SAVE'}
                            </button>
                            <button type='button' onClick={resetForm} disabled={formSubmit}>RESET</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MyLeaves;
